import { Turnstile, TurnstileInstance } from '@marsidev/react-turnstile';
import { Form } from 'antd';
import { useEffect, useRef } from 'react';

const { Item } = Form;

interface TurnstileWidgetProps {
  siteKey: string;
  language?: string;
  /** Toute nouvelle valeur réinitialise le widget (le token Turnstile est à usage unique). */
  resetKey?: number;
  /** Injecté par le `Form.Item` parent. */
  onChange?: (token?: string) => void;
}

const TurnstileWidget = ({
  siteKey,
  language,
  resetKey,
  onChange,
}: TurnstileWidgetProps) => {
  const widgetRef = useRef<TurnstileInstance | undefined>(undefined);
  const previousResetKeyRef = useRef(resetKey);

  useEffect(() => {
    // Sans cette garde, le montage du champ signalerait une valeur vide au
    // formulaire et afficherait l'erreur de validation avant toute interaction.
    if (previousResetKeyRef.current === resetKey) {
      return;
    }
    previousResetKeyRef.current = resetKey;
    widgetRef.current?.reset();
    onChange?.(undefined);
  }, [resetKey, onChange]);

  return (
    <Turnstile
      onError={() => onChange?.(undefined)}
      onExpire={() => onChange?.(undefined)}
      onSuccess={(token) => onChange?.(token)}
      // Le token est porté par le champ du formulaire Ant Design : l'input caché
      // de la librairie ferait doublon dans le DOM du formulaire.
      options={{ language, responseField: false }}
      ref={widgetRef}
      siteKey={siteKey}
    />
  );
};

interface TurnstileFieldProps {
  name: string;
  /** Clé publique du widget ; vide => captcha désactivé, aucun champ n'est rendu. */
  siteKey: string;
  requiredMessage?: string;
  language?: string;
  resetKey?: number;
}

const TurnstileField = ({
  name,
  siteKey,
  requiredMessage,
  language,
  resetKey,
}: TurnstileFieldProps) => {
  if (!siteKey) {
    return null;
  }

  return (
    <Item name={name} rules={[{ required: true, message: requiredMessage }]}>
      <TurnstileWidget
        language={language}
        resetKey={resetKey}
        siteKey={siteKey}
      />
    </Item>
  );
};

export default TurnstileField;
